import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { meQuery, requireAuth } from "@/lib/auth-guard";
import type { UserResponse } from "@/api/auth";
import {
  createFriendCircle,
  deleteFriendCircle,
  mergeFriendCircle,
  removeFriend,
  setFriendCircles,
  updateFriendCircle,
  type FriendCircle,
  type FriendRow,
} from "@/api/friends";
import { revokeInvite } from "@/api/invites";
import { extractApiError, httpStatus } from "@/api/client";
import {
  familiesQuery,
  friendCirclesQuery,
  friendInvitesQuery,
  friendsQuery,
} from "@/lib/queries";
import { expiresInLabel } from "@/lib/format";
import {
  Avatar,
  Card,
  Chip,
  EmptyCard,
  ErrorText,
  Hairline,
  InkButton,
  LoadingBlock,
  QuietButton,
  SectionLabel,
} from "@/components/ui";
import { ConfirmDialog, Modal } from "@/components/Modal";
import { InviteDialog } from "@/components/invites";

/**
 * Friends — mirrors iOS FriendsScreen: a FRIENDS tab (list, search,
 * manage-circles sheet, remove/withdraw/decline) and a CIRCLES tab
 * (create, rename, merge, delete, per-circle members + pending
 * invites). Circle names are private to you; deselecting every circle
 * severs the friendship both ways. All copy verbatim from iOS.
 */

const FriendsPage = () => {
  const { data: user } = useSuspenseQuery<UserResponse>(meQuery);
  return (
    <AppShell user={user}>
      <FriendsView user={user} />
    </AppShell>
  );
};

const FriendsView = ({ user }: { user: UserResponse }) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"friends" | "circles">("friends");
  const friends = useQuery(friendsQuery);
  const circles = useQuery(friendCirclesQuery);
  const invites = useQuery(friendInvitesQuery);
  const families = useQuery(familiesQuery);

  const home = families.data?.find(
    (f) => f.family_id === user.home_family_id,
  );
  // Law 45: minors can't mint invites; delegates may have friend
  // invites turned off by the circle owner.
  const canInvite =
    !user.is_minor && home?.my_permissions.friend_invites !== "none";

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["friends"] });
    void queryClient.invalidateQueries({ queryKey: ["friend-circles"] });
    void queryClient.invalidateQueries({ queryKey: ["friend-invites"] });
  };

  if (friends.isPending || circles.isPending) {
    return <LoadingBlock label="Loading your friends…" />;
  }

  return (
    <div className="mx-auto max-w-2xl">
      <header>
        <h1
          className="font-serif text-[28px] leading-[34px] text-ink"
          style={{ fontWeight: 600 }}
        >
          Friends
        </h1>
        <p className="mt-1 font-serif text-[14px] italic text-ink-soft">
          Your friend circles — only you see your circle names.
        </p>
      </header>

      <div role="tablist" aria-label="Friends sections" className="mt-6 flex gap-2">
        {(
          [
            ["friends", "Friends"],
            ["circles", "Circles"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`tap-target rounded-full px-5 py-2 font-mono text-[12px] uppercase tracking-[0.14em] transition-colors ${
              tab === key
                ? "bg-taupe/25 text-ink"
                : "text-taupe-deep hover:bg-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "friends" ? (
        <FriendsTab
          user={user}
          friends={friends.data ?? []}
          circles={circles.data ?? []}
          canInvite={canInvite}
          onChanged={refresh}
        />
      ) : (
        <CirclesTab
          friends={friends.data ?? []}
          circles={circles.data ?? []}
          invites={invites.data ?? []}
          canInvite={canInvite}
          onChanged={refresh}
        />
      )}
    </div>
  );
};

// ── FRIENDS tab ─────────────────────────────────────────────────────

const friendStatusLine = (f: FriendRow): string | null => {
  if (f.status === "active") return null;
  if (f.status === "pending_guardian") return "Waiting on a parent's approval";
  return f.initiated_by_me
    ? "Invited — waiting on them"
    : "Wants to be your friend";
};

const FriendsTab = ({
  user,
  friends,
  circles,
  canInvite,
  onChanged,
}: {
  user: UserResponse;
  friends: FriendRow[];
  circles: FriendCircle[];
  canInvite: boolean;
  onChanged: () => void;
}) => {
  const [query, setQuery] = useState("");
  const [managing, setManaging] = useState<FriendRow | null>(null);
  const [removing, setRemoving] = useState<FriendRow | null>(null);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const circleName = (id: string) =>
    circles.find((c) => c.circle_id === id)?.name ?? "";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? friends.filter((f) => f.display_name.toLowerCase().includes(q))
      : friends;
  }, [friends, query]);

  const remove = useMutation({
    mutationFn: (f: FriendRow) => removeFriend(f.friendship_id),
    onSuccess: () => {
      setRemoving(null);
      setError(null);
      onChanged();
    },
    onError: (err) => {
      setRemoving(null);
      setError(
        extractApiError(err)?.message ??
          "Couldn't do that right now. Please try again.",
      );
    },
  });

  return (
    <div className="mt-6">
      {friends.length > 0 && (
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search friends"
          aria-label="Search friends"
          className="tap-target mb-4 w-full rounded-full border border-sand bg-white px-5 py-2.5 font-sans text-[15px] outline-none placeholder:text-ink-soft/60 focus:border-taupe"
        />
      )}

      {friends.length === 0 ? (
        <EmptyCard
          blurb={
            canInvite
              ? "No friends yet. Tap Invite a friend below — they'll appear here once they accept."
              : "No friends yet. Friend invites are turned off for your account — your circle owner manages this."
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyCard blurb={`No friends match "${query}".`} />
      ) : (
        <Card>
          {filtered.map((f, i) => {
            const status = friendStatusLine(f);
            return (
              <div key={f.friendship_id}>
                {i > 0 && <Hairline />}
                <div className="flex items-center gap-4 px-5 py-4">
                  <Avatar name={f.display_name} url={f.avatar_url} size={38} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-sans text-[15px] font-medium text-ink">
                      {f.display_name}
                    </p>
                    {status ? (
                      <p className="font-sans text-[13px] text-ink-soft">
                        {status}
                      </p>
                    ) : (
                      <p className="flex flex-wrap gap-1.5 pt-0.5">
                        {f.circle_ids.map((id) => (
                          <Chip key={id}>{circleName(id)}</Chip>
                        ))}
                      </p>
                    )}
                  </div>
                  {f.status === "active" ? (
                    <QuietButton onClick={() => setManaging(f)}>
                      Circles
                    </QuietButton>
                  ) : (
                    <QuietButton onClick={() => setRemoving(f)}>
                      {f.initiated_by_me || f.status === "pending_guardian"
                        ? "Withdraw"
                        : "Decline"}
                    </QuietButton>
                  )}
                </div>
              </div>
            );
          })}
        </Card>
      )}

      <ErrorText>{error}</ErrorText>

      <div className="mt-6">
        {canInvite ? (
          <InkButton onClick={() => setInviting(true)}>
            Invite a friend
          </InkButton>
        ) : (
          !user.is_minor && (
            <p className="font-serif text-[13px] italic text-ink-soft">
              Friend invites are turned off for your account. Your circle
              owner manages this.
            </p>
          )
        )}
      </div>

      {inviting && (
        <FriendInviteDialog
          user={user}
          circles={circles}
          onClose={() => setInviting(false)}
          onCompleted={onChanged}
        />
      )}

      {managing && (
        <ManageFriendDialog
          friend={managing}
          circles={circles}
          onClose={() => setManaging(null)}
          onChanged={() => {
            setManaging(null);
            onChanged();
          }}
        />
      )}

      {removing && (
        <ConfirmDialog
          title={
            removing.status === "active"
              ? `Remove ${removing.display_name}?`
              : removing.initiated_by_me
                ? "Withdraw invitation?"
                : "Decline invitation?"
          }
          body={
            removing.status === "active"
              ? `This ends the friendship for both of you — ${removing.display_name} will no longer see anything you've shared with your circles, and you'll no longer see theirs.`
              : "This cancels the invitation — no friendship is formed."
          }
          confirmLabel={
            removing.status === "active"
              ? "Remove"
              : removing.initiated_by_me
                ? "Withdraw"
                : "Decline"
          }
          destructive
          busy={remove.isPending}
          onConfirm={() => remove.mutate(removing)}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
};

/** Invite-a-friend: pick a destination circle, then mint the link. */
const FriendInviteDialog = ({
  user,
  circles,
  onClose,
  onCompleted,
}: {
  user: UserResponse;
  circles: FriendCircle[];
  onClose: () => void;
  onCompleted: () => void;
}) => {
  const [circleId, setCircleId] = useState<string | undefined>(
    circles[0]?.circle_id,
  );
  const [stage, setStage] = useState<"pick" | "mint">(
    circles.length > 0 ? "pick" : "mint",
  );

  if (stage === "pick") {
    return (
      <Modal title="Invite a friend" onClose={onClose}>
        <p className="font-sans text-[14px] leading-[22px] text-ink-soft">
          Pick the circle they'll join on your side. Only you see your
          circle names — friends never do.
        </p>
        <Card className="mt-4 overflow-hidden border border-sand/40">
          <div role="radiogroup" aria-label="Their circle">
            {circles.map((c, i) => (
              <div key={c.circle_id}>
                {i > 0 && <Hairline />}
                <label className="tap-target flex cursor-pointer items-center gap-4 px-5 py-3.5">
                  <input
                    type="radio"
                    name="friend-circle"
                    checked={circleId === c.circle_id}
                    onChange={() => setCircleId(c.circle_id)}
                    className="h-5 w-5 accent-[#8A7460]"
                  />
                  <span className="font-sans text-[15px] font-medium text-ink">
                    {c.name}
                  </span>
                </label>
              </div>
            ))}
          </div>
        </Card>
        <div className="mt-6 flex justify-end gap-3">
          <QuietButton onClick={onClose}>Cancel</QuietButton>
          <InkButton onClick={() => setStage("mint")} disabled={!circleId}>
            Continue
          </InkButton>
        </div>
      </Modal>
    );
  }

  return (
    <InviteDialog
      familyId={user.home_family_id}
      intent="join_friend_circle"
      circleId={circleId}
      title="Invite a friend"
      onClose={onClose}
      onCompleted={onCompleted}
    />
  );
};

/** Staged circle picker for one friend — iOS FriendManageSheet. */
const ManageFriendDialog = ({
  friend,
  circles,
  onClose,
  onChanged,
}: {
  friend: FriendRow;
  circles: FriendCircle[];
  onClose: () => void;
  onChanged: () => void;
}) => {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(friend.circle_ids),
  );
  const [confirmingAdd, setConfirmingAdd] = useState(false);
  const [confirmingSever, setConfirmingSever] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    selected.size !== friend.circle_ids.length ||
    friend.circle_ids.some((id) => !selected.has(id));
  const added = [...selected].filter(
    (id) => !friend.circle_ids.includes(id),
  );

  const save = useMutation({
    mutationFn: () =>
      selected.size === 0
        ? removeFriend(friend.friendship_id)
        : setFriendCircles(friend.friendship_id, [...selected]).then(
            () => undefined,
          ),
    onSuccess: onChanged,
    onError: (err) => {
      setConfirmingAdd(false);
      setConfirmingSever(false);
      setError(
        extractApiError(err)?.message ??
          "Couldn't save right now. Please try again.",
      );
    },
  });

  const commit = () => {
    if (selected.size === 0) {
      setConfirmingSever(true);
    } else if (added.length > 0) {
      setConfirmingAdd(true);
    } else {
      save.mutate();
    }
  };

  const circleNames = (ids: string[]) =>
    ids
      .map((id) => circles.find((c) => c.circle_id === id)?.name)
      .filter(Boolean)
      .join(", ");

  if (confirmingSever) {
    return (
      <ConfirmDialog
        title={`Remove ${friend.display_name}?`}
        body={`You deselected every circle — this ends the friendship for both of you. ${friend.display_name} would have to be re-invited.`}
        confirmLabel="Remove"
        destructive
        busy={save.isPending}
        onConfirm={() => save.mutate()}
        onCancel={() => setConfirmingSever(false)}
      />
    );
  }

  if (confirmingAdd) {
    return (
      <ConfirmDialog
        title={`Share your history with ${circleNames(added)}?`}
        body={`${friend.display_name} will start seeing everything you've shared with ${added.length === 1 ? "that circle" : "those circles"}.`}
        confirmLabel="Share"
        busy={save.isPending}
        onConfirm={() => save.mutate()}
        onCancel={() => setConfirmingAdd(false)}
      />
    );
  }

  return (
    <Modal title={friend.display_name} onClose={onClose}>
      <SectionLabel>In circles</SectionLabel>
      <Card className="overflow-hidden border border-sand/40">
        {circles.map((c, i) => (
          <div key={c.circle_id}>
            {i > 0 && <Hairline />}
            <label className="tap-target flex cursor-pointer items-center gap-4 px-5 py-3.5">
              <input
                type="checkbox"
                checked={selected.has(c.circle_id)}
                onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) next.add(c.circle_id);
                  else next.delete(c.circle_id);
                  setSelected(next);
                }}
                className="h-5 w-5 accent-[#8A7460]"
              />
              <span className="font-sans text-[15px] font-medium text-ink">
                {c.name}
              </span>
            </label>
          </div>
        ))}
      </Card>
      <p className="mt-3 font-sans text-[13px] text-ink-soft">
        Deselecting every circle removes the friendship for both of you.
      </p>
      <ErrorText>{error}</ErrorText>
      <div className="mt-6 flex justify-end gap-3">
        <QuietButton onClick={onClose}>Cancel</QuietButton>
        <InkButton onClick={commit} disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : "Save"}
        </InkButton>
      </div>
    </Modal>
  );
};

// ── CIRCLES tab ─────────────────────────────────────────────────────

const CirclesTab = ({
  friends,
  circles,
  invites,
  canInvite,
  onChanged,
}: {
  friends: FriendRow[];
  circles: FriendCircle[];
  invites: Array<{
    invite_id: string;
    recipient_display_name: string;
    circle_id: string | null;
    state: string;
    expires_at: string;
    fallback_code: string;
  }>;
  canInvite: boolean;
  onChanged: () => void;
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(
    circles[0]?.circle_id ?? null,
  );
  const [creating, setCreating] = useState(circles.length === 0);
  const [name, setName] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [merging, setMerging] = useState<FriendCircle | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [managing, setManaging] = useState<FriendRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selected =
    circles.find((c) => c.circle_id === selectedId) ?? circles[0] ?? null;
  const showCreate = creating || !selected;

  const create = useMutation({
    mutationFn: () => createFriendCircle(name.trim()),
    onSuccess: (circle) => {
      setName("");
      setCreating(false);
      setSelectedId(circle.circle_id);
      setError(null);
      onChanged();
    },
    onError: (err) =>
      setError(
        httpStatus(err) === 402
          ? "You've reached your plan's circle limit. Upgrade to create more."
          : (extractApiError(err)?.message ??
            "Couldn't create the circle. Please try again."),
      ),
  });

  const merge = useMutation({
    mutationFn: (into: FriendCircle) =>
      mergeFriendCircle(selected!.circle_id, into.circle_id),
    onSuccess: (_res, into) => {
      setMerging(null);
      setSelectedId(into.circle_id);
      setError(null);
      onChanged();
    },
    onError: (err) => {
      setMerging(null);
      setError(
        extractApiError(err)?.message ??
          "Couldn't merge the circles. Please try again.",
      );
    },
  });

  const del = useMutation({
    mutationFn: () => deleteFriendCircle(selected!.circle_id),
    onSuccess: () => {
      setDeleting(false);
      const next = circles.find(
        (c) => c.circle_id !== selected!.circle_id,
      );
      setSelectedId(next?.circle_id ?? null);
      setError(null);
      onChanged();
    },
    onError: (err) => {
      setDeleting(false);
      setError(
        extractApiError(err)?.code === "circle_not_empty"
          ? "Friends who are only in this circle must be moved or removed first."
          : (extractApiError(err)?.message ??
            "Couldn't delete the circle. Please try again."),
      );
    },
  });

  const revoke = useMutation({
    mutationFn: (inviteId: string) => revokeInvite(inviteId),
    onSuccess: onChanged,
    onError: (err) => {
      if (httpStatus(err) === 404 || httpStatus(err) === 409) {
        onChanged();
        return;
      }
      setError(
        extractApiError(err)?.message ??
          "Couldn't cancel that invitation. Please try again.",
      );
    },
  });

  const members = selected
    ? friends.filter(
        (f) =>
          f.status === "active" &&
          f.circle_ids.includes(selected.circle_id),
      )
    : [];
  const circleInvites = selected
    ? invites.filter(
        (inv) =>
          inv.circle_id === selected.circle_id &&
          (inv.state === "pending" || inv.state === "clicked"),
      )
    : [];

  return (
    <div className="mt-6">
      {/* Circle pills */}
      <div className="flex flex-wrap items-center gap-2">
        {circles.map((c) => (
          <button
            key={c.circle_id}
            type="button"
            onClick={() => {
              setSelectedId(c.circle_id);
              setCreating(false);
            }}
            aria-pressed={!showCreate && selected?.circle_id === c.circle_id}
            className={`tap-target rounded-full px-4 py-2 font-sans text-[14px] font-medium transition-colors ${
              !showCreate && selected?.circle_id === c.circle_id
                ? "bg-ink text-white"
                : "bg-white text-ink-soft hover:bg-sand/30"
            }`}
          >
            {c.name}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setCreating(true)}
          aria-label="Create a circle"
          className={`tap-target flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
            showCreate
              ? "bg-ink text-white"
              : "bg-white text-taupe-deep hover:bg-sand/30"
          }`}
        >
          +
        </button>
      </div>

      {showCreate ? (
        <div className="mt-6">
          <Card className="p-6">
            <p className="font-serif text-[17px] font-medium text-ink">
              A circle is your private guest list
            </p>
            <p className="mt-3 font-serif text-[14px] italic leading-[22px] text-ink-soft">
              Friend circles group the people you share with — "Ski crew,"
              "College friends," whatever fits. Only you see your circle
              names — friends never do.
            </p>
            <p className="mt-2 font-serif text-[14px] italic leading-[22px] text-ink-soft">
              You choose what flows to each circle: everything, only the
              memories you pick, or nothing — change it anytime under
              Sharing.
            </p>
            <form
              className="mt-5 flex flex-wrap gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) create.mutate();
              }}
            >
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Circle name — e.g. College friends"
                className="tap-target min-w-0 flex-1 rounded-2xl border border-sand bg-paper px-4 py-3 font-sans text-[15px] outline-none placeholder:text-ink-soft/60 focus:border-taupe"
              />
              <InkButton type="submit" disabled={!name.trim() || create.isPending}>
                {create.isPending ? "Creating…" : "Create circle"}
              </InkButton>
            </form>
          </Card>
        </div>
      ) : (
        selected && (
          <div className="mt-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SectionLabel>
                {selected.name} · {members.length} in this circle
              </SectionLabel>
              <div className="flex gap-2">
                <QuietButton onClick={() => setRenaming(true)}>
                  Rename
                </QuietButton>
                {circles.length > 1 && (
                  <QuietButton
                    onClick={() =>
                      setMerging(
                        circles.find(
                          (c) => c.circle_id !== selected.circle_id,
                        ) ?? null,
                      )
                    }
                  >
                    Merge into…
                  </QuietButton>
                )}
                <QuietButton onClick={() => setDeleting(true)}>
                  Delete circle
                </QuietButton>
              </div>
            </div>

            {members.length === 0 && circleInvites.length === 0 ? (
              <Card className="mt-3 p-6">
                <p className="font-serif text-[17px] font-medium text-ink">
                  No one here yet
                </p>
                <p className="mt-3 font-serif text-[14px] italic leading-[22px] text-ink-soft">
                  Invite someone from the Friends tab and pick this circle
                  for them. When they accept, they choose a circle for you
                  on their side too — then you'll both see what the other
                  shares.
                </p>
                <p className="mt-2 font-serif text-[14px] italic leading-[22px] text-ink-soft">
                  Nothing is shared until they accept — and what you share
                  is always up to you, under Sharing.
                </p>
              </Card>
            ) : (
              <>
                {members.length > 0 && (
                  <Card className="mt-3">
                    {members.map((f, i) => (
                      <div key={f.friendship_id}>
                        {i > 0 && <Hairline />}
                        <div className="flex items-center gap-4 px-5 py-4">
                          <Avatar
                            name={f.display_name}
                            url={f.avatar_url}
                            size={38}
                          />
                          <p className="min-w-0 flex-1 truncate font-sans text-[15px] font-medium text-ink">
                            {f.display_name}
                          </p>
                          <QuietButton onClick={() => setManaging(f)}>
                            Circles
                          </QuietButton>
                        </div>
                      </div>
                    ))}
                  </Card>
                )}

                {circleInvites.length > 0 && (
                  <div className="mt-5">
                    <SectionLabel>Waiting on a reply</SectionLabel>
                    <Card>
                      {circleInvites.map((inv, i) => (
                        <div key={inv.invite_id}>
                          {i > 0 && <Hairline />}
                          <div className="flex flex-wrap items-center gap-3 px-5 py-4">
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-sans text-[15px] font-medium text-ink">
                                Invitation for {inv.recipient_display_name}
                              </p>
                              <p className="mt-0.5 font-sans text-[13px] text-ink-soft">
                                <span className="font-mono uppercase tracking-[0.18em] text-taupe-deep">
                                  {inv.fallback_code}
                                </span>
                                {" · "}
                                {expiresInLabel(inv.expires_at)}
                              </p>
                            </div>
                            {canInvite && (
                              <QuietButton
                                onClick={() => revoke.mutate(inv.invite_id)}
                                disabled={revoke.isPending}
                              >
                                Cancel
                              </QuietButton>
                            )}
                          </div>
                        </div>
                      ))}
                    </Card>
                  </div>
                )}
              </>
            )}
          </div>
        )
      )}

      <ErrorText>{error}</ErrorText>

      {renaming && selected && (
        <RenameCircleDialog
          circle={selected}
          onClose={() => setRenaming(false)}
          onDone={() => {
            setRenaming(false);
            onChanged();
          }}
        />
      )}

      {merging && selected && (
        <MergePickerDialog
          source={selected}
          targets={circles.filter(
            (c) => c.circle_id !== selected.circle_id,
          )}
          busy={merge.isPending}
          onConfirm={(target) => merge.mutate(target)}
          onClose={() => setMerging(null)}
        />
      )}

      {deleting && selected && (
        <ConfirmDialog
          title={`Delete ${selected.name}?`}
          body={
            <>
              Memories you've shared with this circle stop being visible to
              it. Friends who are also in other circles keep those; friends
              who are only here must be moved or removed first.
              {circleInvites.length > 0 &&
                ` This also cancels ${circleInvites.length} outstanding invite${circleInvites.length === 1 ? "" : "s"} into this circle.`}
            </>
          }
          confirmLabel="Delete circle"
          destructive
          busy={del.isPending}
          onConfirm={() => del.mutate()}
          onCancel={() => setDeleting(false)}
        />
      )}

      {managing && (
        <ManageFriendDialog
          friend={managing}
          circles={circles}
          onClose={() => setManaging(null)}
          onChanged={() => {
            setManaging(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
};

const RenameCircleDialog = ({
  circle,
  onClose,
  onDone,
}: {
  circle: FriendCircle;
  onClose: () => void;
  onDone: () => void;
}) => {
  const [name, setName] = useState(circle.name);
  const [error, setError] = useState<string | null>(null);

  const rename = useMutation({
    mutationFn: () =>
      updateFriendCircle(circle.circle_id, { name: name.trim() }),
    onSuccess: onDone,
    onError: (err) =>
      setError(
        extractApiError(err)?.message ??
          "Couldn't rename the circle. Please try again.",
      ),
  });

  return (
    <Modal title="Rename circle" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) rename.mutate();
        }}
      >
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. College friends"
          autoFocus
          className="tap-target w-full rounded-2xl border border-sand bg-paper px-4 py-3 font-sans text-[15px] outline-none placeholder:text-ink-soft/60 focus:border-taupe"
        />
        <p className="mt-2 font-sans text-[13px] text-ink-soft">
          Only you see your circle names — friends never do.
        </p>
        <ErrorText>{error}</ErrorText>
        <div className="mt-6 flex justify-end gap-3">
          <QuietButton onClick={onClose}>Cancel</QuietButton>
          <InkButton type="submit" disabled={!name.trim() || rename.isPending}>
            {rename.isPending ? "Saving…" : "Save"}
          </InkButton>
        </div>
      </form>
    </Modal>
  );
};

const MergePickerDialog = ({
  source,
  targets,
  busy,
  onConfirm,
  onClose,
}: {
  source: FriendCircle;
  targets: FriendCircle[];
  busy: boolean;
  onConfirm: (target: FriendCircle) => void;
  onClose: () => void;
}) => {
  const [target, setTarget] = useState<FriendCircle | null>(
    targets[0] ?? null,
  );
  const [confirming, setConfirming] = useState(false);

  if (confirming && target) {
    return (
      <ConfirmDialog
        title={`Merge ${source.name} into ${target.name}?`}
        body={`Everyone here joins ${target.name}, and everything you've shared with either circle stays visible to all of them. Outstanding invites follow too. This circle goes away.`}
        confirmLabel="Merge"
        busy={busy}
        onConfirm={() => onConfirm(target)}
        onCancel={() => setConfirming(false)}
      />
    );
  }

  return (
    <Modal title={`Merge ${source.name} into…`} onClose={onClose}>
      <Card className="overflow-hidden border border-sand/40">
        <div role="radiogroup" aria-label="Merge into">
          {targets.map((c, i) => (
            <div key={c.circle_id}>
              {i > 0 && <Hairline />}
              <label className="tap-target flex cursor-pointer items-center gap-4 px-5 py-3.5">
                <input
                  type="radio"
                  name="merge-target"
                  checked={target?.circle_id === c.circle_id}
                  onChange={() => setTarget(c)}
                  className="h-5 w-5 accent-[#8A7460]"
                />
                <span className="font-sans text-[15px] font-medium text-ink">
                  {c.name}
                </span>
              </label>
            </div>
          ))}
        </div>
      </Card>
      <div className="mt-6 flex justify-end gap-3">
        <QuietButton onClick={onClose}>Cancel</QuietButton>
        <InkButton onClick={() => setConfirming(true)} disabled={!target}>
          Continue
        </InkButton>
      </div>
    </Modal>
  );
};

export const Route = createFileRoute("/friends")({
  loader: ({ context }) => requireAuth(context.queryClient),
  component: FriendsPage,
});
